import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FaBell, FaBellSlash, FaCheck, FaCheckDouble, FaTrash, FaSearch,
  FaTimes, FaUserShield, FaClipboardList, FaHandshake, FaCog,
  FaEnvelope, FaExclamationTriangle, FaCalendarAlt, FaStar,
  FaFileAlt, FaCircle, FaArrowRight, FaToggleOn, FaToggleOff,
  FaSpinner, FaCheckCircle, FaFilter,
} from 'react-icons/fa';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import Navbar  from '../components/Navbar';
import { useTheme } from '../context/ThemeContext';

import { API_URL as API } from '../config/api';
const SB_OPEN  = 252; const SB_CLOSED = 74; const SB_GAP = 12;
const NB_HEIGHT = 68; const NB_TOP = 12;

//  Config types 
const TYPES = {
  devis:       { label: 'Devis',         color: '#fd7e14', bg: 'rgba(253,126,20,0.09)',  icon: <FaFileAlt />          },
  contact:     { label: 'Contact',        color: '#0d6efd', bg: 'rgba(13,110,253,0.09)', icon: <FaEnvelope />         },
  inscription: { label: 'Inscription',    color: '#198754', bg: 'rgba(25,135,84,0.09)',  icon: <FaCheckCircle />      },
  partenariat: { label: 'Partenariat',    color: '#6f42c1', bg: 'rgba(111,66,193,0.09)', icon: <FaHandshake />        },
  alerte:      { label: 'Alerte',         color: '#dc3545', bg: 'rgba(220,53,69,0.09)',  icon: <FaExclamationTriangle/> },
  formation:   { label: 'Formation',      color: '#fd7e14', bg: 'rgba(253,126,20,0.09)', icon: <FaCalendarAlt />      },
  admin:       { label: 'Administrateur', color: '#6f42c1', bg: 'rgba(111,66,193,0.09)', icon: <FaUserShield />       },
  systeme:     { label: 'Système',        color: '#20c997', bg: 'rgba(32,201,151,0.09)', icon: <FaCog />              },
};

const PRIORITES = {
  haute:   { label: 'Haute',   color: '#dc3545' },
  normale: { label: 'Normale', color: '#fd7e14' },
  basse:   { label: 'Basse',   color: '#8fa3b5' },
};

const fmtRelTime = d => {
  const diff = Math.floor((Date.now() - new Date(d)) / 60000);
  if (diff < 1)    return "À l'instant";
  if (diff < 60)   return `Il y a ${diff} min`;
  if (diff < 1440) return `Il y a ${Math.floor(diff / 60)}h`;
  if (diff < 2880) return 'Hier';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getTypeCfg = t => TYPES[t] || { label: t, color: '#8fa3b5', bg: 'rgba(143,163,181,0.09)', icon: <FaBell /> };

//  Toast léger ─
const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((msg, type = 'success') => {
    const id = Date.now();
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3500);
  }, []);
  return { toasts, add };
};
const ToastContainer = ({ toasts }) => (
  <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8 }}>
    {toasts.map(t => (
      <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, minWidth: 260, background: t.type === 'success' ? 'linear-gradient(135deg,#0d1f14,#071a10)' : 'linear-gradient(135deg,#1f0d0d,#1a0707)', border: `1px solid ${t.type === 'success' ? 'rgba(25,135,84,.4)' : 'rgba(220,53,69,.4)'}`, boxShadow: '0 12px 30px rgba(0,0,0,.3)', animation: 'nt-toast .3s ease' }}>
        <span style={{ color: t.type === 'success' ? '#20c997' : '#dc3545', fontSize: 14 }}>{t.type === 'success' ? '✓' : '✕'}</span>
        <p style={{ flex: 1, fontSize: 13, fontWeight: 500, color: '#eef2f7', margin: 0 }}>{t.msg}</p>
      </div>
    ))}
    <style>{`@keyframes nt-toast{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}`}</style>
  </div>
);

//  Préférences (locale, extensible) ─
const PREF_INIT = {
  devis_new:         { label: 'Nouvelle demande de devis',       on: true  },
  contact_new:       { label: 'Nouveau message de contact',      on: true  },
  inscription_new:   { label: 'Nouvelle inscription',            on: true  },
  partenariat_new:   { label: 'Nouvelle demande de partenariat', on: true  },
  partenariat_status:{ label: 'Changement de statut partenariat',on: false },
  admin_connexion:   { label: 'Connexion suspecte admin',        on: true  },
  admin_creation:    { label: 'Création d\'un administrateur',   on: true  },
  formation_new:     { label: 'Nouvelle formation planifiée',    on: true  },
  systeme_backup:    { label: 'Sauvegarde automatique',          on: false },
  systeme_erreur:    { label: 'Erreurs système critiques',       on: true  },
};

// ─
const Notifications = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { toasts, add: toast } = useToast();

  const token   = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  //  State ─
  const [notifs,       setNotifs]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [unreadCount,  setUnreadCount]  = useState(0);
  const [totalItems,   setTotalItems]   = useState(0);
  const [currentPage,  setCurrentPage]  = useState(1);
  const [lastPage,     setLastPage]     = useState(1);

  const [search,       setSearch]       = useState('');
  const [filterType,   setFilterType]   = useState('all');
  const [filterLu,     setFilterLu]     = useState('all');
  const [activeTab,    setActiveTab]    = useState('notifs');
  const [prefs,        setPrefs]        = useState(PREF_INIT);
  const [detail,       setDetail]       = useState(null);
  const [actionLoad,   setActionLoad]   = useState(null);

  //  Thème ─
  const c = isDark ? {
    bg: '#090c16', card: 'rgba(255,255,255,0.04)', cardB: 'rgba(255,255,255,0.08)',
    text: '#c8d4e0', textDim: '#5a6b82', title: '#eef2f7',
    surface: 'rgba(255,255,255,0.035)', input: 'rgba(255,255,255,0.06)', inputB: 'rgba(255,255,255,0.12)',
    shadow: '0 4px 24px rgba(0,0,0,0.4)', divider: 'rgba(255,255,255,0.07)',
    unreadBg: 'rgba(25,135,84,0.06)', unreadBd: 'rgba(25,135,84,0.15)',
    overlay: 'rgba(9,12,22,0.85)', modalBg: '#0f172a', tabOn: 'rgba(255,255,255,0.08)',
  } : {
    bg: '#f0f3f8', card: '#ffffff', cardB: 'rgba(0,0,0,0.07)',
    text: '#4d6070', textDim: '#8fa3b5', title: '#1a2535',
    surface: 'rgba(0,0,0,0.025)', input: '#f7f9fb', inputB: 'rgba(0,0,0,0.08)',
    shadow: '0 2px 12px rgba(0,0,0,0.05)', divider: 'rgba(0,0,0,0.07)',
    unreadBg: 'rgba(25,135,84,0.04)', unreadBd: 'rgba(25,135,84,0.12)',
    overlay: 'rgba(15,23,42,0.65)', modalBg: '#ffffff', tabOn: '#ffffff',
  };

  const mainML = `${SB_GAP + (sidebarOpen ? SB_OPEN : SB_CLOSED) + 12}px`;
  const mainPT = `${NB_TOP + NB_HEIGHT + 12}px`;

  //  Fetch ─
  const fetchNotifs = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/notifications`, {
      headers,
      params: {
        page:     currentPage,
        per_page: 15,
        type:     filterType !== 'all' ? filterType : undefined,
        lu:       filterLu   !== 'all' ? filterLu   : undefined,
        search:   search || undefined,
      },
    }).then(({ data }) => {
      setNotifs(data.data);
      setTotalItems(data.total);
      setLastPage(data.last_page);
      setUnreadCount(data.unread_count);
    }).finally(() => setLoading(false));
  }, [currentPage, filterType, filterLu, search, token]);

  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);
  useEffect(() => { setCurrentPage(1); }, [filterType, filterLu, search]);

  //  Actions ─
  const markRead = async (id, silent = false) => {
    try {
      await axios.patch(`${API}/admin/notifications/${id}/read`, {}, { headers });
      setNotifs(p => p.map(n => n.id === id ? { ...n, lu: true } : n));
      setUnreadCount(p => Math.max(0, p - 1));
      if (detail?.id === id) setDetail(d => ({ ...d, lu: true }));
      if (!silent) toast('Marqué comme lu');
    } catch { if (!silent) toast('Erreur', 'error'); }
  };

  const markUnread = async (id) => {
    try {
      await axios.patch(`${API}/admin/notifications/${id}/unread`, {}, { headers });
      setNotifs(p => p.map(n => n.id === id ? { ...n, lu: false } : n));
      setUnreadCount(p => p + 1);
      if (detail?.id === id) setDetail(d => ({ ...d, lu: false }));
      toast('Marqué comme non lu');
    } catch { toast('Erreur', 'error'); }
  };

  const deleteNotif = async (id) => {
    setActionLoad('del-' + id);
    try {
      await axios.delete(`${API}/admin/notifications/${id}`, { headers });
      const n = notifs.find(x => x.id === id);
      setNotifs(p => p.filter(x => x.id !== id));
      if (n && !n.lu) setUnreadCount(p => Math.max(0, p - 1));
      setTotalItems(p => p - 1);
      if (detail?.id === id) setDetail(null);
      toast('Notification supprimée');
    } catch { toast('Erreur', 'error'); }
    finally { setActionLoad(null); }
  };

  const markAllRead = async () => {
    setActionLoad('readAll');
    try {
      await axios.post(`${API}/admin/notifications/read-all`, {}, { headers });
      setNotifs(p => p.map(n => ({ ...n, lu: true })));
      setUnreadCount(0);
      toast('Toutes les notifications marquées comme lues');
    } catch { toast('Erreur', 'error'); }
    finally { setActionLoad(null); }
  };

  const deleteAll = async () => {
    if (!window.confirm('Supprimer toutes les notifications ? Cette action est irréversible.')) return;
    setActionLoad('delAll');
    try {
      await axios.delete(`${API}/admin/notifications/all`, { headers });
      setNotifs([]); setUnreadCount(0); setTotalItems(0);
      setDetail(null);
      toast('Toutes les notifications supprimées');
    } catch { toast('Erreur', 'error'); }
    finally { setActionLoad(null); }
  };

  const openDetail = (n) => {
    setDetail(n);
    if (!n.lu) markRead(n.id, true);
  };

  const togglePref = key => setPrefs(p => ({ ...p, [key]: { ...p[key], on: !p[key].on } }));

  //  Groupes par date 
  const groups = useMemo(() => {
    const today     = new Date(); today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
    const g = { "Aujourd'hui": [], 'Hier': [], 'Cette semaine': [], 'Plus ancien': [] };
    notifs.forEach(n => {
      const d = new Date(n.date); d.setHours(0, 0, 0, 0);
      if (d.getTime() === today.getTime())         g["Aujourd'hui"].push(n);
      else if (d.getTime() === yesterday.getTime()) g['Hier'].push(n);
      else if ((today - d) < 7 * 86400000)          g['Cette semaine'].push(n);
      else                                           g['Plus ancien'].push(n);
    });
    return Object.entries(g).filter(([, v]) => v.length > 0);
  }, [notifs]);

  const Spin = () => <FaSpinner style={{ animation: 'nt-spin .8s linear infinite', fontSize: 13 }} />;
  const inProg = k => actionLoad === k;

  return (
    <div style={{ background: c.bg, minHeight: '100vh', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;font-family:'Plus Jakarta Sans',sans-serif}
        @keyframes nt-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes nt-in{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
        @keyframes nt-pop{from{opacity:0;transform:scale(0.93) translateY(12px)}to{opacity:1;transform:none}}
        @keyframes nt-fo{from{opacity:0}to{opacity:1}}
        @keyframes nt-shimmer{from{background-position:200% 0}to{background-position:-200% 0}}
        .nt-main{transition:margin-left .28s cubic-bezier(.4,0,.2,1);min-height:100vh}
        .nt-body{padding:22px 24px 48px;animation:nt-in .35s ease}
        .nt-tabs{display:flex;gap:4px;background:${c.surface};border:1px solid ${c.cardB};border-radius:12px;padding:4px;width:fit-content;margin-bottom:20px}
        .nt-tab{display:flex;align-items:center;gap:8px;padding:10px 20px;border-radius:9px;border:none;font-size:13.5px;font-weight:600;cursor:pointer;transition:all .2s}
        .nt-tab.on{background:${c.tabOn};color:#198754;box-shadow:${isDark?'none':'0 2px 8px rgba(0,0,0,.06)'}}
        .nt-tab.off{background:transparent;color:${c.textDim}}
        .nt-tab.off:hover{color:${c.title}}
        .nt-badge{min-width:18px;height:18px;border-radius:9px;background:#dc3545;color:#fff;font-size:10px;font-weight:800;display:flex;align-items:center;justify-content:center;padding:0 5px}
        .nt-filter-btn{height:40px;padding:0 14px;border-radius:9px;border:1.5px solid ${c.cardB};background:${c.card};font-family:'Plus Jakarta Sans',sans-serif;font-size:12.5px;font-weight:600;color:${c.text};cursor:pointer;transition:all .18s;white-space:nowrap}
        .nt-filter-btn.on{background:#198754;color:#fff;border-color:#198754}
        .nt-filter-btn:hover:not(.on){border-color:#198754;color:#198754}
        .nt-act-btn{display:inline-flex;align-items:center;gap:7px;padding:8px 15px;border-radius:9px;font-size:13px;font-weight:600;cursor:pointer;transition:all .18s;border:1.5px solid;background:transparent;font-family:'Plus Jakarta Sans',sans-serif}
        .nt-act-btn:disabled{opacity:.45;cursor:not-allowed}
        .nt-item{display:flex;align-items:flex-start;gap:13px;padding:15px 17px;border-radius:13px;border:1px solid ${c.cardB};margin-bottom:7px;cursor:pointer;transition:all .18s;background:${c.card};position:relative}
        .nt-item.unread{border-color:${c.unreadBd};background:${c.unreadBg}}
        .nt-item:hover{border-color:#198754;box-shadow:0 4px 16px rgba(25,135,84,.08);transform:translateY(-1px)}
        .nt-item-actions{display:flex;gap:5px;flex-shrink:0;opacity:0;transition:opacity .18s}
        .nt-item:hover .nt-item-actions{opacity:1}
        .nt-ia{width:28px;height:28px;border-radius:7px;border:1px solid ${c.cardB};background:transparent;display:flex;align-items:center;justify-content:center;font-size:11px;cursor:pointer;transition:all .15s;color:${c.textDim}}
        .nt-ia-g:hover{background:rgba(25,135,84,.1);color:#198754;border-color:rgba(25,135,84,.2)}
        .nt-ia-r:hover{background:rgba(220,53,69,.1);color:#dc3545;border-color:rgba(220,53,69,.2)}
        .nt-unread-dot{width:8px;height:8px;border-radius:50%;background:#198754;flex-shrink:0;margin-top:5px;box-shadow:0 0 0 3px rgba(25,135,84,.15)}
        .nt-group-label{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.8px;color:${c.textDim};padding:10px 0 8px;display:flex;align-items:center;gap:10px}
        .nt-group-label::after{content:'';flex:1;height:1px;background:${c.divider}}
        .nt-type-tag{display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:600;padding:2px 8px;border-radius:6px}
        .nt-prio{display:inline-flex;align-items:center;gap:4px;font-size:10.5px;font-weight:700;padding:2px 7px;border-radius:5px}
        .nt-pref-section{background:${c.card};border:1px solid ${c.cardB};border-radius:12px;overflow:hidden;margin-bottom:14px;box-shadow:${c.shadow}}
        .nt-pref-hd{padding:12px 16px;border-bottom:1px solid ${c.divider};font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.7px;color:${c.textDim};border-left:3px solid #198754}
        .nt-pref-item{display:flex;align-items:center;justify-content:space-between;padding:13px 16px;border-bottom:1px solid ${c.divider}}
        .nt-pref-item:last-child{border-bottom:none}
        .nt-pref-label{font-size:14px;font-weight:500;color:${c.title}}
        .nt-toggle{background:none;border:none;cursor:pointer;font-size:26px;transition:color .2s;padding:0}
        .nt-overlay{position:fixed;inset:0;backdrop-filter:blur(7px);background:${c.overlay};display:flex;align-items:center;justify-content:center;z-index:2000;padding:20px;animation:nt-fo .2s ease}
        .nt-modal{background:${c.modalBg};border:1px solid ${c.cardB};border-radius:18px;width:100%;max-width:560px;box-shadow:0 24px 60px rgba(0,0,0,.2);animation:nt-pop .26s cubic-bezier(.34,1.56,.64,1);overflow:hidden}
        .nt-modal-hd{padding:18px 22px;border-bottom:1px solid ${c.divider};display:flex;align-items:center;justify-content:space-between;background:${isDark?'rgba(255,255,255,.03)':'#fafbfc'}}
        .nt-modal-body{padding:20px 22px}
        .nt-cx{width:32px;height:32px;border-radius:8px;border:1.5px solid ${c.cardB};background:transparent;display:flex;align-items:center;justify-content:center;font-size:13px;color:${c.textDim};cursor:pointer;transition:all .18s}
        .nt-cx:hover{background:#dc3545;color:#fff;border-color:#dc3545}
        .sk{height:80px;border-radius:13px;margin-bottom:7px;background:linear-gradient(90deg,rgba(128,128,128,.06) 25%,rgba(128,128,128,.12) 50%,rgba(128,128,128,.06) 75%);background-size:200% 100%;animation:nt-shimmer 1.4s infinite}
        .pg-btn{min-width:36px;height:36px;border-radius:9px;font-size:13px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0 10px;transition:all .18s;font-family:'Plus Jakarta Sans',sans-serif}
        .pg-btn:hover:not(:disabled){border-color:#198754!important;color:#198754!important}
        .pg-btn:disabled{opacity:.3;cursor:default}
        @media(max-width:1024px){.nt-main{margin-left:0!important}}
        @media(max-width:768px){.nt-body{padding:14px}.nt-modal{max-width:100%;border-radius:14px}}
      `}</style>

      <ToastContainer toasts={toasts} />
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      <Navbar  sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} pageTitle="Notifications" />

      <main className="nt-main" style={{ marginLeft: mainML, paddingTop: mainPT }}>
        <div className="nt-body">

          {/*  En-tête  */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22, gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 48, height: 48, borderRadius: 13, background: '#198754', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, position: 'relative', flexShrink: 0 }}>
                <FaBell />
                {unreadCount > 0 && (
                  <div style={{ position: 'absolute', top: -5, right: -5, minWidth: 18, height: 18, borderRadius: 9, background: '#dc3545', color: '#fff', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px', border: `2px solid ${c.bg}` }}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </div>
                )}
              </div>
              <div>
                <h1 style={{ fontSize: 21, fontWeight: 800, color: c.title, letterSpacing: '-.3px', marginBottom: 2 }}>Notifications</h1>
                <p style={{ fontSize: 13, color: c.textDim }}>
                  {unreadCount > 0 && <><strong style={{ color: '#dc3545' }}>{unreadCount}</strong> non lue{unreadCount > 1 ? 's' : ''} · </>}
                  {totalItems} au total
                </p>
              </div>
            </div>

            {/* Tabs */}
            <div className="nt-tabs">
              <button className={`nt-tab ${activeTab === 'notifs' ? 'on' : 'off'}`} onClick={() => setActiveTab('notifs')}>
                <FaBell style={{ fontSize: 13 }} />Notifications
                {unreadCount > 0 && <span className="nt-badge">{unreadCount}</span>}
              </button>
              <button className={`nt-tab ${activeTab === 'prefs' ? 'on' : 'off'}`} onClick={() => setActiveTab('prefs')}>
                <FaCog style={{ fontSize: 13 }} />Préférences
              </button>
            </div>
          </div>

          {/*  NOTIFICATIONS  */}
          {activeTab === 'notifs' && (
            <>
              {/* Filtres */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: 220, display: 'flex', alignItems: 'center', gap: 9, background: c.card, border: `1.5px solid ${c.cardB}`, borderRadius: 10, padding: '0 13px', height: 42 }}>
                  <FaSearch style={{ color: c.textDim, fontSize: 12, flexShrink: 0 }} />
                  <input style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13.5, background: 'transparent', color: c.title }} placeholder="Rechercher une notification…" value={search} onChange={e => setSearch(e.target.value)} />
                  {search && <FaTimes style={{ color: c.textDim, cursor: 'pointer', fontSize: 11 }} onClick={() => setSearch('')} />}
                </div>

                {/* Type */}
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  <button className={`nt-filter-btn ${filterType === 'all' ? 'on' : ''}`} onClick={() => setFilterType('all')}>Tous</button>
                  {Object.entries(TYPES).map(([k, v]) => (
                    <button key={k} className={`nt-filter-btn ${filterType === k ? 'on' : ''}`}
                      style={filterType === k ? {} : { color: v.color, borderColor: `${v.color}30` }}
                      onClick={() => setFilterType(k)}>
                      {v.label}
                    </button>
                  ))}
                </div>

                {/* Lu/non lu */}
                <div style={{ display: 'flex', gap: 5 }}>
                  {[['all', 'Tout'], ['unread', 'Non lues'], ['read', 'Lues']].map(([v, l]) => (
                    <button key={v} className={`nt-filter-btn ${filterLu === v ? 'on' : ''}`} onClick={() => setFilterLu(v)}>{l}</button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                <button className="nt-act-btn" style={{ color: '#198754', borderColor: 'rgba(25,135,84,.2)' }} onClick={markAllRead} disabled={!!actionLoad || unreadCount === 0}>
                  {inProg('readAll') ? <Spin /> : <FaCheckDouble style={{ fontSize: 11 }} />} Tout marquer comme lu
                </button>
                <button className="nt-act-btn" style={{ color: '#dc3545', borderColor: 'rgba(220,53,69,.2)' }} onClick={deleteAll} disabled={!!actionLoad || totalItems === 0}>
                  {inProg('delAll') ? <Spin /> : <FaTrash style={{ fontSize: 11 }} />} Tout supprimer
                </button>
                <span style={{ fontSize: 12, color: c.textDim, fontWeight: 600, marginLeft: 'auto' }}>
                  {totalItems} notification{totalItems > 1 ? 's' : ''}
                </span>
              </div>

              {/* Liste groupée */}
              {loading ? (
                [...Array(6)].map((_, i) => <div key={i} className="sk" />)
              ) : groups.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '56px 20px', background: c.card, border: `1.5px dashed ${c.cardB}`, borderRadius: 14 }}>
                  <FaBellSlash style={{ fontSize: 44, color: c.textDim, opacity: .3, display: 'block', margin: '0 auto 12px' }} />
                  <p style={{ fontSize: 15, fontWeight: 700, color: c.title, marginBottom: 5 }}>Aucune notification</p>
                  <p style={{ fontSize: 13, color: c.textDim }}>Vous êtes à jour !</p>
                </div>
              ) : (
                groups.map(([groupName, items]) => (
                  <div key={groupName}>
                    <div className="nt-group-label">{groupName}</div>
                    {items.map(n => {
                      const tp = getTypeCfg(n.type);
                      const pr = PRIORITES[n.priorite] || PRIORITES.normale;
                      return (
                        <div key={n.id} className={`nt-item${!n.lu ? ' unread' : ''}`} onClick={() => openDetail(n)}>
                          <div style={{ width: 40, height: 40, borderRadius: 11, background: tp.bg, color: tp.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 }}>{tp.icon}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 3 }}>
                              <div style={{ fontSize: 13.5, fontWeight: !n.lu ? 800 : 600, color: c.title, lineHeight: 1.4 }}>{n.titre}</div>
                              <div className="nt-item-actions" onClick={e => e.stopPropagation()}>
                                {n.lu
                                  ? <button className="nt-ia nt-ia-g" title="Marquer non lu" onClick={() => markUnread(n.id)}><FaCircle style={{ fontSize: 7 }} /></button>
                                  : <button className="nt-ia nt-ia-g" title="Marquer lu"     onClick={() => markRead(n.id)}><FaCheck /></button>
                                }
                                <button className="nt-ia nt-ia-r" title="Supprimer" onClick={() => deleteNotif(n.id)} disabled={inProg('del-'+n.id)}>
                                  {inProg('del-'+n.id) ? <Spin /> : <FaTrash />}
                                </button>
                              </div>
                            </div>
                            <div style={{ fontSize: 13, color: c.text, lineHeight: 1.6, marginBottom: 7, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{n.message}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 11.5, color: c.textDim, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <FaCalendarAlt style={{ fontSize: 9 }} />{fmtRelTime(n.date)}
                              </span>
                              <span className="nt-type-tag" style={{ color: tp.color, background: tp.bg }}>{tp.icon} {tp.label}</span>
                              {n.priorite && n.priorite !== 'normale' && (
                                <span className="nt-prio" style={{ color: pr.color, background: `${pr.color}12`, border: `1px solid ${pr.color}25` }}>
                                  <FaCircle style={{ fontSize: 5 }} />{pr.label}
                                </span>
                              )}
                            </div>
                          </div>
                          {!n.lu && <div className="nt-unread-dot" />}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}

              {/* Pagination */}
              {lastPage > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '14px 0', flexWrap: 'wrap' }}>
                  <button className="pg-btn" style={{ border: `1.5px solid ${c.cardB}`, background: c.card, color: c.text }} onClick={() => setCurrentPage(p => Math.max(p-1,1))} disabled={currentPage===1}>‹</button>
                  {[...Array(lastPage)].map((_, i) => i+1).map(p => (
                    <button key={p} className="pg-btn" onClick={() => setCurrentPage(p)}
                      style={{ border: `1.5px solid ${currentPage===p?'#198754':c.cardB}`, background: currentPage===p?'#198754':c.card, color: currentPage===p?'#fff':c.text }}>
                      {p}
                    </button>
                  ))}
                  <button className="pg-btn" style={{ border: `1.5px solid ${c.cardB}`, background: c.card, color: c.text }} onClick={() => setCurrentPage(p => Math.min(p+1,lastPage))} disabled={currentPage===lastPage}>›</button>
                  <span style={{ fontSize: 12, color: c.textDim, fontWeight: 600, marginLeft: 'auto' }}>Page {currentPage}/{lastPage}</span>
                </div>
              )}
            </>
          )}

          {/*  PRÉFÉRENCES  */}
          {activeTab === 'prefs' && (
            <div style={{ maxWidth: 640 }}>
              <p style={{ fontSize: 14, color: c.textDim, marginBottom: 20, lineHeight: 1.7 }}>
                Choisissez les événements pour lesquels vous souhaitez recevoir des notifications dans le tableau de bord.
              </p>
              {[
                { label: 'Devis & Contacts',  keys: ['devis_new', 'contact_new'] },
                { label: 'Inscriptions',       keys: ['inscription_new'] },
                { label: 'Partenariats',       keys: ['partenariat_new', 'partenariat_status'] },
                { label: 'Administrateurs',    keys: ['admin_connexion', 'admin_creation'] },
                { label: 'Formations',         keys: ['formation_new'] },
                { label: 'Système',            keys: ['systeme_backup', 'systeme_erreur'] },
              ].map(group => (
                <div key={group.label} className="nt-pref-section">
                  <div className="nt-pref-hd">{group.label}</div>
                  {group.keys.map(key => (
                    <div key={key} className="nt-pref-item">
                      <div className="nt-pref-label">{prefs[key].label}</div>
                      <button className="nt-toggle" style={{ color: prefs[key].on ? '#198754' : c.textDim }} onClick={() => togglePref(key)}>
                        {prefs[key].on ? <FaToggleOn /> : <FaToggleOff />}
                      </button>
                    </div>
                  ))}
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', background: '#198754', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}
                  onClick={() => toast('Préférences enregistrées ✓')}>
                  <FaCheck />Enregistrer les préférences
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* ════ MODAL DÉTAIL ════ */}
      {detail && (
        <div className="nt-overlay" onClick={() => setDetail(null)}>
          <div className="nt-modal" onClick={e => e.stopPropagation()}>
            <div className="nt-modal-hd">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: getTypeCfg(detail.type).bg, color: getTypeCfg(detail.type).color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                  {getTypeCfg(detail.type).icon}
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: c.title, lineHeight: 1.4 }}>{detail.titre}</div>
                  <div style={{ fontSize: 12, color: c.textDim, marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <span>{fmtRelTime(detail.date)}</span>
                    <span className="nt-type-tag" style={{ color: getTypeCfg(detail.type).color, background: getTypeCfg(detail.type).bg, padding: '1px 7px', borderRadius: 5 }}>
                      {getTypeCfg(detail.type).label}
                    </span>
                  </div>
                </div>
              </div>
              <button className="nt-cx" onClick={() => setDetail(null)}><FaTimes /></button>
            </div>

            <div className="nt-modal-body">
              <div style={{ background: c.surface, border: `1px solid ${c.cardB}`, borderRadius: 10, padding: '14px 16px', fontSize: 14.5, color: c.text, lineHeight: 1.8, marginBottom: 16 }}>
                {detail.message}
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {detail.lu ? (
                  <button style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'transparent', border: `1px solid ${c.cardB}`, borderRadius: 8, fontSize: 13, fontWeight: 600, color: c.textDim, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}
                    onClick={() => { markUnread(detail.id); }}>
                    <FaCircle style={{ fontSize: 8 }} />Marquer non lu
                  </button>
                ) : (
                  <button style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'rgba(25,135,84,.08)', border: '1px solid rgba(25,135,84,.2)', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#198754', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}
                    onClick={() => { markRead(detail.id); }}>
                    <FaCheck />Marquer lu
                  </button>
                )}
                <button style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'rgba(220,53,69,.06)', border: '1px solid rgba(220,53,69,.18)', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#dc3545', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}
                  onClick={() => deleteNotif(detail.id)}>
                  <FaTrash style={{ fontSize: 11 }} />Supprimer
                </button>
              </div>

              {detail.lien && (
                <a href={detail.lien} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(25,135,84,.06)', border: '1px solid rgba(25,135,84,.15)', borderRadius: 10, fontSize: 13.5, fontWeight: 600, color: '#198754', textDecoration: 'none', transition: 'all .18s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(25,135,84,.1)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(25,135,84,.06)'}>
                  <span>Accéder à la page concernée</span>
                  <FaArrowRight />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;